import React, {
	memo,
	ChangeEvent,
	useState,
	RefObject,
	useCallback,
	useRef,
	useEffect,
} from 'react'
import Grid from '@material-ui/core/Grid'
import FileCopyIcon from '@material-ui/icons/FileCopy'
import { makeStyles } from '@material-ui/core/styles'
import { useTranslation } from 'react-i18next'
import Fab from '@material-ui/core/Fab'
import axios from 'axios'
import cuid from 'cuid'
import { useMutation } from '@apollo/react-hooks'
import Chip from '@material-ui/core/Chip'
import DeleteIcon from '@material-ui/icons/DeleteOutlineOutlined'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogTitle from '@material-ui/core/DialogTitle'
import Button from '@material-ui/core/Button'

import styles from './styles'
import { StoreValue, SetCVFile } from 'store'
import { EAppStateActionTypes } from 'models'
import { SIGN_REQUEST_URIS, buildUserCvFile } from 'utils'
import { Loading } from 'components'

const useStyles = makeStyles(styles)

const CvFilesView: React.FunctionComponent<any> = ({
	storeUserId,
	storeUserCvFilePath = [],
	storeUserCvFileOriginalName = [],
	storeDispatch,
}) => {
	const classes = useStyles()
	const { t } = useTranslation()
	const uploadInput: RefObject<HTMLInputElement> = useRef(null)
	const [stage, setStage] = useState(
		storeUserCvFileOriginalName.length > 0 ? 4 : 0,
	)
	const [percent, update] = useState(0)
	const [openDeleteModal, setOpenDeleteModal] = useState(false)
	const [confirmDelete, setConfirmDelete] = useState(false)

	const [handleSetCvFile] = useMutation(SetCVFile, {
		variables: {
			cvFilePath: storeUserCvFilePath,
			cvFileOriginalName: storeUserCvFileOriginalName,
		},
	})

	const handleFileSelect = useCallback(
		(ev: ChangeEvent<HTMLInputElement>) => {
			setStage(1)
			const source: any =
				uploadInput &&
				uploadInput.current &&
				uploadInput.current.files &&
				uploadInput.current.files[0]
			const blob = source.slice(0, source.size, source.type)
			const fileId = cuid()
			// setFileId(fileId)
			const file = new File([blob], `${fileId}${source.name}`, {
				type: source.type,
			})
			const params = {
				userId: storeUserId,
				fileName: file && file.name,
				contentType: file && file.type,
				environment: process.env.REACT_APP_ENVIRONMENT,
				type: 'user',
			}
			axios({
				method: 'post',
				// @ts-ignore
				url: SIGN_REQUEST_URIS[process.env.REACT_APP_ENVIRONMENT],
				data: params,
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
			})
				.then(({ data: { signedUrl } }: any) => {
					setStage(3)
					var options = {
						headers: {
							'Content-Type': file && file.type,
						},
						onUploadProgress: (progressEvent: any) => {
							let newPercent: any =
								(progressEvent.loaded / progressEvent.total) *
								100
							if (newPercent > 100) newPercent = 100
							update(newPercent.toFixed(0))
						},
					}
					axios
						.put(signedUrl, file, options)
						.then(() => {
							storeDispatch({
								type: EAppStateActionTypes.setUserCvFile,
								userCv: {
									cvFilePath: [file.name],
									cvFileOriginalName: [source.name],
								},
							})
							setStage(4)
							handleSetCvFile({
								variables: {
									cvFilePath: [file.name],
									cvFileOriginalName: [source.name],
								},
							})
							// @ts-ignore
							document.getElementById('cvFile').value = ''
						})
						.catch(error => {
							console.error(error)
							// @ts-ignore
							document.getElementById('cvFile').value = ''
						})
				})
				.catch(error => {
					setStage(2)
					console.error(error)
					// @ts-ignore
					document.getElementById('cvFile').value = ''
				})
		},
		[storeUserId, storeDispatch, handleSetCvFile],
	)
	const triggerFileSelect = useCallback(() => {
		if (uploadInput && uploadInput.current) uploadInput.current.click()
	}, [uploadInput])
	const handleOpenCvFile = useCallback(() => {
		window.open(
			buildUserCvFile(storeUserId, storeUserCvFilePath[0]),
			'_blank',
		)
	}, [storeUserId, storeUserCvFilePath])

	const toggleDeleteModal = useCallback(() => {
		setOpenDeleteModal(openDeleteModal => !openDeleteModal)
	}, [])
	const toggleConfirmDelete = useCallback(() => {
		setConfirmDelete(confirmDelete => !confirmDelete)
	}, [])

	let buttonText: any = ''
	switch (stage) {
		case 1:
			buttonText = t('Preparing ...')
			break
		case 2:
			buttonText = t('Something went wrong ... try again')
			break
		case 3:
			buttonText = t('Uploading ... ') + percent + '%'
			break
		default:
			buttonText = t('Add a cv file!')
	}

	useEffect(() => {
		if (confirmDelete) {
			storeDispatch({
				type: EAppStateActionTypes.setUserCvFile,
				userCv: {
					cvFilePath: [],
					cvFileOriginalName: [],
				},
			})
			handleSetCvFile({
				variables: {
					cvFileOriginalName: [],
					cvFilePath: [],
				},
			})
			setConfirmDelete(false)
			setOpenDeleteModal(false)
			setStage(0)
		}
	}, [confirmDelete, handleSetCvFile, storeDispatch])

	return (
		<>
			<Grid item xs={12}>
				<div className={classes.container}>
					<div className={classes.cvWrap}>
						<div className={classes.cvFile}>
							<FileCopyIcon fontSize={'large'} />
							{stage === 4 ? (
								<div>
									<div style={{ cursor: 'pointer' }}>
										<span className={classes.fileName}>
											{t('My CV: ')}
										</span>
										<span
											className={classes.fileName}
											onClick={handleOpenCvFile}
										>
											{storeUserCvFileOriginalName[0]}
										</span>
									</div>
								</div>
							) : (
								<div>
									<span className={classes.fileName}>
										{buttonText}
									</span>
								</div>
							)}
						</div>
						{stage > 0 && stage < 4 && (
							<div className={classes.uploadFileLoader}>
								<Loading size={20} />
							</div>
						)}
						<div className={classes.actionButtons}>
							<>
								{storeUserCvFileOriginalName &&
									storeUserCvFilePath &&
									storeUserCvFilePath.length > 0 &&
									storeUserCvFileOriginalName.length > 0 && (
										<Chip
											onClick={toggleDeleteModal}
											style={{
												margin: 2,
												background: '#fff',
											}}
											icon={<DeleteIcon />}
											variant="outlined"
											color={'secondary'}
											clickable={true}
											classes={{
												label: classes.editIconLabel,
												root: classes.deleteRootElement,
											}}
										/>
									)}
								<input
									onChange={handleFileSelect}
									ref={uploadInput}
									id="cvFile"
									type="file"
									style={{ display: 'none' }}
								/>
								<Fab
									onClick={triggerFileSelect}
									className={classes.uploadNewBtn}
									variant="extended"
									size="small"
									color="primary"
									aria-label="Add"
								>
									{t('Upload New CV') || ''}
								</Fab>
							</>
						</div>
					</div>
				</div>
			</Grid>
			<Dialog
				open={openDeleteModal}
				onClose={toggleDeleteModal}
				aria-labelledby="responsive-dialog-title"
			>
				<DialogTitle id="responsive-dialog-title">
					{t('Are you sure you want to delete this cv file?')}
				</DialogTitle>
				<DialogActions>
					<Button color="primary" onClick={toggleConfirmDelete}>
						{t('Yes')}
					</Button>
					<Button
						color="primary"
						autoFocus
						onClick={toggleDeleteModal}
					>
						{t('No')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}

const CvFiles = memo((props: any) => (
	<StoreValue
		keys={[
			'storeDispatch',
			'user.id|storeUserId',
			'user.profile.cvFilePath|storeUserCvFilePath',
			'user.profile.cvFileOriginalName|storeUserCvFileOriginalName',
		]}
	>
		<CvFilesView {...props} />
	</StoreValue>
))

export default CvFiles
